import { useEffect, useMemo, useRef, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Check, Dices, RotateCcw, Sparkles } from "lucide-react";
import { seo } from "@/lib/seo";
import { CosmicShell } from "@/components/whatif/cosmic-shell";
import { WhatIfGate } from "@/components/whatif/whatif-gate";
import { CertaintyBadge } from "@/components/whatif/certainty-badge";
import { useWhatIfScenarios } from "@/hooks/use-whatif-scenarios";
import { CATEGORY_BY_ID } from "@/lib/whatif/categories";
import { pickRandom } from "@/lib/whatif/dataset";
import { pushRecentScenario } from "@/lib/whatif/recent";
import { readProgress, saveProgress } from "@/lib/whatif/progress";
import { pulseAmbient } from "@/lib/whatif/ambient";
import { StageVisual } from "@/components/whatif/stage-visual";
import type { WhatIfScenario } from "@/lib/whatif/types";

export const Route = createFileRoute("/what-if/$slug")({
  head: () =>
    seo({
      title: "WHAT IF? LAB — Scenario | The Quantum Mind",
      description:
        "A short, stage-by-stage science story: change one rule and follow what would really happen, with every claim labelled by certainty.",
      noindex: true,
    }),
  component: ScenarioPage,
});

function ScenarioStory({ scenario, all }: { scenario: WhatIfScenario; all: WhatIfScenario[] }) {
  const navigate = useNavigate();
  const total = scenario.stages.length;
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState<1 | -1>(1);
  const [drag, setDrag] = useState(0);
  const [resumeAt, setResumeAt] = useState<number | null>(null);
  const [completedBefore, setCompletedBefore] = useState(false);

  // Local-only "recently explored" history + resume offer. No backend calls.
  useEffect(() => {
    setIndex(0);
    setDir(1);
    pushRecentScenario(scenario.slug);
    const saved = readProgress(scenario.slug);
    setCompletedBefore(Boolean(saved?.done));
    setResumeAt(saved && !saved.done && saved.stage > 0 && saved.stage < total ? saved.stage : null);
  }, [scenario.slug, total]);

  const finished = index >= total;
  const stage = scenario.stages[Math.min(index, total - 1)];
  const touch = useRef<{ x: number; y: number } | null>(null);
  const related = useMemo(
    () =>
      scenario.relatedScenarioIds
        .map((id) => all.find((s) => s.id === id))
        .filter((s): s is WhatIfScenario => Boolean(s))
        .slice(0, 3),
    [scenario, all],
  );

  // Persist the furthest point reached, locally.
  useEffect(() => {
    saveProgress(scenario.slug, Math.min(index, total - 1), index >= total);
    if (index >= total) setCompletedBefore(true);
  }, [scenario.slug, index, total]);

  const surprise = () => {
    const pick = pickRandom(all, scenario.slug);
    if (pick) void navigate({ to: "/what-if/$slug", params: { slug: pick.slug } });
  };

  const go = (delta: number) => {
    setIndex((i) => {
      const next = Math.max(0, Math.min(total, i + delta));
      if (next !== i) {
        setDir(delta > 0 ? 1 : -1);
        setResumeAt(null);
        pulseAmbient();
      }
      return next;
    });
  };

  const jumpTo = (target: number) => {
    setDir(target >= index ? 1 : -1);
    setResumeAt(null);
    pulseAmbient();
    setIndex(Math.max(0, Math.min(total, target)));
  };

  // Keyboard navigation — ignored while typing or while a control has focus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && el.closest("button, a, input, textarea, select, [contenteditable=true]")) return;
      if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [total]);

  // Swipe navigation — horizontal intent only, never from a control.
  const onTouchStart = (e: React.TouchEvent) => {
    if ((e.target as HTMLElement).closest("button, a, input, select, textarea")) {
      touch.current = null;
      return;
    }
    const t = e.changedTouches[0];
    touch.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const start = touch.current;
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // Live lean feedback, capped so the card never leaves the viewport.
    if (Math.abs(dx) > Math.abs(dy)) setDrag(Math.max(-24, Math.min(24, dx * 0.28)));
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current;
    touch.current = null;
    setDrag(0);
    if (!start) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < 56 || Math.abs(dx) < Math.abs(dy) * 1.6) return;
    go(dx < 0 ? 1 : -1);
  };

  const enterClass = dir === 1 ? "whatif-enter-next" : "whatif-enter-prev";

  return (
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
      <div className="mt-5 flex items-center gap-3">
        <ol className="flex flex-1 items-center gap-1.5" aria-label="Scenario progress">
          {scenario.stages.map((s, i) => (
            <li key={s.title} className="flex-1">
              <button
                type="button"
                onClick={() => jumpTo(i)}
                aria-current={i === index ? "step" : undefined}
                aria-label={`Go to stage ${i + 1} of ${total}: ${s.title}`}
                className="block w-full py-2"
              >
                <span
                  className={`block h-1.5 w-full rounded-full transition-all duration-500 ${
                    i === index
                      ? "bg-[linear-gradient(90deg,var(--wi-electric),var(--wi-cyan))]"
                      : i < index
                        ? "bg-[color:var(--wi-cyan)]/60"
                        : "bg-white/12"
                  }`}
                />
              </button>
            </li>
          ))}
        </ol>
        {completedBefore ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-300/30 bg-emerald-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-200">
            <Check className="size-3" /> Done
          </span>
        ) : null}
      </div>

      <p aria-live="polite" className="sr-only">
        {finished
          ? `End of scenario: ${scenario.title}. Related scenarios available.`
          : `Stage ${index + 1} of ${total}: ${stage.title}. Certainty: ${stage.certainty}.`}
      </p>

      {resumeAt !== null && !finished ? (
        <div className="whatif-surface mt-3 flex items-center justify-between gap-3 rounded-2xl px-4 py-3">
          <p className="text-xs text-[color:var(--wi-ink-dim)]">
            You stopped at stage {resumeAt + 1}.
          </p>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => jumpTo(resumeAt)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[linear-gradient(90deg,#3b6dff,#7c4dff)] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.1em] text-white active:scale-[0.97]"
            >
              <RotateCcw className="size-3" /> Resume
            </button>
            <button
              type="button"
              onClick={() => setResumeAt(null)}
              className="rounded-full border border-[color:var(--wi-line)] px-3 py-1.5 text-[11px] font-semibold text-[color:var(--wi-ink-dim)]"
            >
              Dismiss
            </button>
          </div>
        </div>
      ) : null}

      {finished ? (
        <section
          key="end"
          className={`${enterClass} whatif-surface relative mt-5 overflow-hidden rounded-[22px] p-5`}
        >
          <span aria-hidden className="whatif-bloom" />
          <div className="relative">
            <div className="flex items-center gap-3">
              <svg viewBox="0 0 48 48" className="size-11 shrink-0" aria-hidden focusable="false">
                <circle
                  className="whatif-ring"
                  cx="24"
                  cy="24"
                  r="16"
                  fill="none"
                  stroke="var(--wi-cyan)"
                  strokeWidth="1"
                />
                <circle cx="24" cy="24" r="11" fill="rgb(79 214 232 / 0.14)" />
                <path
                  d="M18 24.5l4.2 4.2L30.5 20"
                  fill="none"
                  stroke="var(--wi-cyan)"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>
                <h2 className="whatif-display text-xl font-bold">Scenario complete</h2>
                <p className="mt-0.5 text-sm text-[color:var(--wi-ink-dim)]">
                  {total} stages explored — where next?
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={surprise}
              className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-[linear-gradient(90deg,#3b6dff,#7c4dff)] text-sm font-bold uppercase tracking-[0.12em] text-white active:scale-[0.98]"
            >
              <Dices className="size-4" /> Surprise me
            </button>
            <ul className="mt-4 space-y-2">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to="/what-if/$slug"
                    params={{ slug: r.slug }}
                    className="flex items-center gap-2 rounded-2xl border border-[color:var(--wi-line)] px-3.5 py-3 text-sm font-medium active:scale-[0.99]"
                  >
                    <span aria-hidden>{CATEGORY_BY_ID.get(r.category)?.emoji}</span>
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => jumpTo(total - 1)}
                className="h-11 flex-1 rounded-2xl border border-[color:var(--wi-line)] text-sm font-semibold"
              >
                ← Previous
              </button>
              {related[0] ? (
                <Link
                  to="/what-if/$slug"
                  params={{ slug: related[0].slug }}
                  className="flex h-11 flex-1 items-center justify-center rounded-2xl bg-white/10 text-sm font-semibold"
                >
                  Next What If →
                </Link>
              ) : null}
            </div>
          </div>
        </section>
      ) : (
        <article
          key={index}
          style={drag ? { transform: `translate3d(${drag}px,0,0)` } : undefined}
          className={`${enterClass} whatif-swipe whatif-surface mt-5 rounded-[22px] p-5`}
        >
          <div className="flex items-center justify-between gap-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--wi-ink-dim)]">
              Stage {index + 1} / {total}
            </p>
            <CertaintyBadge value={stage.certainty} />
          </div>
          <h2 className="whatif-display mt-2 text-2xl font-bold leading-tight">{stage.title}</h2>
          <p className="mt-3 text-sm leading-relaxed text-[color:var(--wi-ink-dim)]">{stage.body}</p>
          <StageVisual stage={stage} step={index} total={total} />
          {stage.surprise ? (
            <div className="mt-4 rounded-2xl border border-[color:var(--wi-line)] bg-[linear-gradient(120deg,rgb(79_214_232/0.08),rgb(124_77_255/0.08))] p-3.5">
              <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--wi-cyan)]">
                <Sparkles className="size-3" /> Surprise
              </p>
              <p className="mt-1.5 text-xs leading-relaxed text-[color:var(--wi-ink)]">
                {stage.surprise}
              </p>
            </div>
          ) : null}
        </article>
      )}

      {!finished ? (
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            disabled={index === 0}
            onClick={() => go(-1)}
            className="h-12 flex-1 rounded-2xl border border-[color:var(--wi-line)] text-sm font-semibold disabled:opacity-40"
          >
            ← Previous
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            className="h-12 flex-1 rounded-2xl bg-[linear-gradient(90deg,#3b6dff,#7c4dff)] text-sm font-semibold text-white active:scale-[0.98]"
          >
            {index === total - 1 ? "Finish ✓" : "Next →"}
          </button>
        </div>
      ) : null}
      <p className="mt-3 text-center text-[10px] text-[color:var(--wi-ink-dim)]">
        Swipe or use ← → keys to move between stages
      </p>
    </div>
  );
}

function ScenarioPage() {
  const { slug } = Route.useParams();
  const { scenarios } = useWhatIfScenarios();
  const scenario = scenarios?.find((s) => s.slug === slug);

  return (
    <CosmicShell>
      <Link
        to="/what-if"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[color:var(--wi-ink-dim)]"
      >
        <ArrowLeft className="size-3.5" /> What If? Lab
      </Link>

      {!scenarios ? (
        <div className="whatif-surface mt-6 h-64 animate-pulse rounded-[22px]" />
      ) : !scenario ? (
        <div className="whatif-surface mt-6 rounded-[22px] p-6 text-center">
          <p className="whatif-display text-base font-bold">That scenario doesn&apos;t exist</p>
          <Link
            to="/what-if"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--wi-cyan)]"
          >
            Back to the lab <ArrowRight className="size-3.5" />
          </Link>
        </div>
      ) : (
        <>
          <header className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--wi-ink-dim)]">
              {CATEGORY_BY_ID.get(scenario.category)?.emoji}{" "}
              {CATEGORY_BY_ID.get(scenario.category)?.name}
            </p>
            <h1 className="whatif-display mt-1 text-2xl font-bold leading-tight">
              {scenario.title}
            </h1>
          </header>
          <WhatIfGate>
            <ScenarioStory scenario={scenario} all={scenarios} />
          </WhatIfGate>
        </>
      )}
    </CosmicShell>
  );
}